import { useState } from 'react';

import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import { getFormDefaultValue, getTableHeaders } from '../../utils/forms/FormDataModel';
import { ContentItem, OnAddArguments, TabContent } from '../../utils/forms/FormInterface';
import AbstractForm from '../Entity/AbstractForm';
import AbstractLanding from '../Entity/AbstractLanding';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import { REVIEWS_PAGE_ENTITY } from '../../constants/EntityName';
import { ReviewsFormData } from '../../datamodels/Reviews';
import callApi from '../../Api/config';
import { toastError, toastSuccess } from '../../service/Toast';
import { BASE_IMAGE_URL } from '../../env';

interface ReviewFormData {
  title: string;
  description: string;
  iconImage?: string | string[];
  [key: string]: any;
}

const ReviewsPage = () => {
  const [activeKey, setActiveKey] = useState<string>('0');
  const [userName, setUserName] = useState<string>();
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [content, setContent] = useState<TabContent>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentFormData, setCurrentFormData] = useState<ReviewFormData | null>(null);

  const defaultFormValue: ReviewFormData = getFormDefaultValue(ReviewsFormData);
  const tableHeaders: any = getTableHeaders(ReviewsFormData);
  const addName = 'Review';
  const headerName = 'Review';

  const onSuccessful = (id?: string) => {
    if (id === 'create') {
      setShowFormComponent(false);
    }
    onTabClick('0');
    setRefreshData(prev => !prev);
  };

  const onAddNew = (argument1?: OnAddArguments) => {
    if (argument1) {
      const checkArr = content.filter((item: ContentItem) => item.key === argument1.id);
      checkArr.length ? null : onAdd(argument1);
    } else {
      setShowFormComponent(true);
      setUserName('Add Review');
      onTabClick('a');
    }
  };

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && setRefreshData(prev => !prev);
    key === 'a' && setShowFormComponent(false);
  };

  const onAdd = (argument1: OnAddArguments) => {
    if (!argument1.id) return;

    const newObj = {
      key: argument1.id,
      tabTitle: argument1.id,
      content: (
        <GenericAccordian key={content.length}>
          <AbstractForm
            entityId={argument1.id}
            isCreateMode={false}
            headerName={headerName}
            entityName={REVIEWS_PAGE_ENTITY}
            formTemplate={ReviewsFormData}
            formDefaultPayload={getFormDefaultValue(ReviewsFormData)}
            onSuccessfulUpdate={() => {
              if (currentFormData) {
                handleUpdate(argument1.id!, currentFormData);
              }
            }}
            formHeader=""
            showDeleteDraft={true}
            closeTab={closeTab}
            payloadProcessor={(formData: ReviewFormData) => {
              setCurrentFormData(formData);
              const payload = { ...formData };
              if (payload.iconImage && Array.isArray(payload.iconImage)) {
                payload.iconImage = payload.iconImage[0];
              }
              return payload;
            }}
          />
        </GenericAccordian>
      ),
    };

    setContent((prev: any) => [...prev, newObj]);
    setActiveKey(argument1.id);
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        setShowFormComponent(false);
        onTabClick('0');
      } else {
        onTabClick('0');
        setContent(content => content.filter(item => String(item.key) !== targetKey));
      }
    }
  };

  const handleCreate = async (formData: ReviewFormData) => {
    try {
      setLoading(true);
      const response = await callApi(REVIEWS_PAGE_ENTITY, 'POST', formData);
      if (response?.status) {
        toastSuccess('Review created successfully');
        onSuccessful('create');
      } else {
        toastError(response?.data?.message || 'Failed to create review');
      }
    } catch (error) {
      console.error('Create review error:', error);
      toastError('Failed to create review');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: string, formData: ReviewFormData) => {
    try {
      setLoading(true);
      const response = await callApi(`${REVIEWS_PAGE_ENTITY}/${id}`, 'PATCH', formData);
      if (response?.status) {
        toastSuccess('Review updated successfully');
        onSuccessful('update');
      } else {
        toastError(response?.data?.message || 'Failed to update review');
      }
    } catch (error) {
      console.error('Update review error:', error);
      toastError('Failed to update review');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setLoading(true);
      const response = await callApi(`${REVIEWS_PAGE_ENTITY}/${id}`, 'DELETE');
      if (response?.status) {
        toastSuccess('Review deleted successfully');
        onSuccessful('delete');
      } else {
        toastError(response?.data?.message || 'Failed to delete review');
      }
    } catch (error) {
      console.error('Delete review error:', error);
      toastError('Failed to delete review');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Review"
      updateFormVisibility={updateFormVisibility}
      onTabClick={onTabClick}
      secondTabTitle={userName}
      showFormComponent={showFormComponent}
      content={content}
      formComponent={
        <GenericAccordian>
          <AbstractForm
            isCreateMode={true}
            headerName={headerName}
            entityName={REVIEWS_PAGE_ENTITY}
            formTemplate={ReviewsFormData}
            formDefaultPayload={defaultFormValue}
            onSuccessfulUpdate={() => {
              if (currentFormData) {
                handleCreate(currentFormData);
              }
            }}
            closeTab={closeTab}
            payloadProcessor={(formData: ReviewFormData) => {
              setCurrentFormData(formData);
              const payload = { ...formData };
              if (payload.iconImage && Array.isArray(payload.iconImage)) {
                payload.iconImage = payload.iconImage[0];
              }
              return payload;
            }}
          />
        </GenericAccordian>
      }
    >
      <AbstractLanding
        headerName={headerName}
        addName={addName}
        entityName={REVIEWS_PAGE_ENTITY}
        tableHeaders={tableHeaders}
        onAdd={onAddNew}
        refreshData={refreshData}
      />
    </AbstractTabs>
  );
};

export default ReviewsPage;
